import { CensusManager } from "@/employer/types";
import {
  Address,
  EmployerPayer,
  EmployerSuspension,
  FetchState,
  FundingOption,
  InvestmentCustodian,
  InvestmentThreshold,
  PartnerBilling,
  PartnerFeeWaiver,
  PendingPersonalContribution,
  TransactionEditSearchResult,
  Advisor3Rule,
  AdvisorRulePreviewResult,
  AdvisorDelayType,
  AdvisorDeliveryChannel,
  AdvisorDeliveryPage,
  AdvisorDelayPage,
  AdvisorPreviewTemplateResponse,
  InvestmentFeeSchedule,
  AllowedInvestmentAccountTypes,
  AllowedEmployerInvestmentAccountTypes,
  InstrumentDetail,
  EnrollmentOption,
  FeeCollectionType,
  EmploymentStatus,
  LinkedAccountType,
  LinkedBankAccountVerificationStatus,
  ReimbursementType,
  DrivewealthAccountManagementType,
  UserHsaAccountStatus,
  PartnerHsaStoreInfo,
  FeeCollectionTypeState,
  UserSearchDocument,
  EmployerSearchDocument,
  InvestmentFeeScheduleState,
  CardProvider,
  FeePackageModel,
  UserInterestRate,
  EmployerFeeSchedule,
  PurseStackingOrderModel,
  HsaStoreVisibility,
  BrowserIdentifier
} from "@/shared/types";
import { bool } from "aws-sdk/clients/signer";
import moment from "moment";

export interface EmployerState {
  employers: Employer[];
  employerPages: Employer[][];
  employersFetchState: FetchState;
  suspensionsFetch: FetchState;
  suspensions: EmployerSuspension[];
  billing: {[employerId: number]: EmployerPayer};
  employerFundingDays: {[employerId: number]: number};
  employerTdaPlanId: {[employerId: number]: string};
  investmentThreshold: {[employerId: number]: InvestmentThreshold};
  investmentEnrollmentType: {[employerId: number]: EmployerInvestmentEnrollmentTypeState};
  investmentFeeCollectionType: {[employerId: number]: FeeCollectionTypeState};
  investmentFeeSchedules: {[employerId: number]: InvestmentFeeScheduleState};
  censusManagerFetchState: FetchState;
  pageNumber: number;
  totalRecords: number;
  employerSearchResult: EmployerSearchResult;
  employerSearchFetchState: FetchState;
  ebtoEmployerSearchResult: EbtoEmployerSearchResult;
  ebtoEmployerSearchFetchState: FetchState;
  employerMigrationPeriod: EmployerMigrationPeriod;
  employerMigrationPeriodFetchState: FetchState;
  retailContributionWarning: {[employerId: number]: boolean};
}
export interface EmployerSearchResult {
  employers: EmployerSearchResponse[];
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
  partnerId: number;
  maxScore: number;
  queryTime: number;
  includeTerminated: boolean;
}
export interface EmployerSearchResponse {
  id: number;
  name: string;
  partnerName: string;
  phone: string;
  dateCreated: string;
  dateActivated: string;
  employerStatus: EmployerStatusType;
  fundingOption: FundingOption;
  externalEmployerId: string;
  employerTaxId: string;
}
export enum EmployerStatusType {
    Pending = "pending",
    Activated = "activated",
    Terminated = "terminated",
    Reactivated = "reactivated",
    Autoactivated = "autoactivated",
}
export const employerStatusTypeMap: Record<EmployerStatusType, string> = {
  pending: "Pending",
  activated: "Activated",
  terminated: "Terminated",
  reactivated: "Reactivated",
  autoactivated: "Auto-Activated"
};
export interface Employer {
  id: number;
  name: string;
  phone: string;
  dateCreated: string;
  dateActivated: string;
  isEmployerActivated: boolean;
  isEmployerAutoActivated: boolean;
  enrollmentOption: EnrollmentOption;
  fundingOption: FundingOption;
  partner?: EmployerPartner;
  servicingPartners?: ServicingPartner[];
  fundingDays: number;
  fundingDaysState: FetchState;
  tdaPlanId: string;
  tdaPlanIdState: FetchState;
  salesPersonId?: number;
  hubspotId: string;
  censusManager: CensusManager;
  isTerminated: boolean;
  enrolledActiveCount: number;
  dateTerminationEffective: string;
  dateTerminated: string;
  allowHealthPlanSyncOverride: boolean;
  ssoUserSyncStatus: boolean;
}

export interface ServicingPartner {
  partnerId: number;
  partnerName: string;
  isSelected: boolean;
}

export interface EmployerPartner {
  id: number;
  name?: string;
  custodian?: BankCustodian;
  ssoDisabled?: boolean;
  isTalonDisabledForPartner?: boolean;
  ssoUserSyncStatus?: boolean;
}

export interface EmployerListResult {
  employers: Employer[];
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
}

export interface Page {
  hasMore: boolean;
  items: Employer[];
  isLoading: boolean;
}

export interface EmployerActivationRequest {
  employerId: number;
  fundingOption: FundingOption;
}

export interface PartnerThemeResponse {
  partnerId: number;
  themeId: number;
  partnerThemeId: number;
}

export interface PartnerThemeRequest {
  partnerId: number | string;
  themeId?: number;
  partnerThemeId?: number;
}

export interface EmployerStatusChangeRequest {
  reason: string;
  employerId: number;
  terminationReasonId: number;
  dateTerminationEffective: string;
}

export interface TerminationReason {
  terminationReasonId: number;
  description: string;
}

export interface TerminationReasonState {
  fetchState: FetchState;
  terminationReasons: TerminationReason[];
}

export interface SelectOption {
  id: number;
  name: string;
  description: string;
}

export interface RuleOptions {
  attributes: SelectOption[];
  comparisonOperators: SelectOption[];
  logicalOperators: SelectOption[];
  deliveryChannels: SelectOption[];
  deliveryPages: SelectOption[];
  personaTypes: SelectOption[];
  campaignTypes: SelectOption[];
  modules: SelectOption[];
  delayTypes: SelectOption[];
  dataTypes: SelectOption[];
}

export interface PartnerState {
  fetchState: FetchState;
  tpaIdGetFetchState: FetchState;
  tpaIdPostFetchState: FetchState;
  pageNumber: number;
  partners: PartnerListItem[];
  partner: PartnerSave;
  tpaIds: string[];
  feeWaiverFetchState: FetchState;
  feeWaiver: PartnerFeeWaiver;
  investmentFeePartnerId: number;
  investmentFeeScheduleState: FetchState;
  investmentFeeSchedules: InvestmentFeeSchedule[];
  investmentThresholds: PartnerInvestmentThreshold[];
  investmentThresholdFetchState: FetchState;
  achCompanyNameFetchState: FetchState;
  achCompanyName: string;
  bulkFilesFetchState: FetchState;
  bulkFiles: PartnerUserBulkResponse;
}

export interface PendingTxState {
  accountName: string;
  fetchState: FetchState;
  pendingTxs: AdminPendingTx[];
}

export interface SftpTestFilesState {
  canDownload: boolean;
  fetchState: FetchState;
  testFiles: SftpTestFile[];
}

export interface SftpTestFile {
  lastModified: string;
  s3Key: string;
}

export interface PartnerGetResponse {
  partners: {
    id: number;
    name: string;
    dateCreated: string;
    dateSignedUp?: string;
    dateTerminated?: string;
    canViewEmployerSsns: boolean;
    usersAreAutomaticallyRedirectedToWex: boolean;
    custodian: BankCustodian;
  }[];
}

export interface PartnerListItem {
  id: number;
  name: string;
  dateCreated: moment.Moment;
  dateSignedUp?: moment.Moment;
  dateTerminated?: moment.Moment;
  canViewEmployerSsns: boolean;
  usersAreAutomaticallyRedirectedToWex: boolean;
  custodian: BankCustodian;
}

export interface AdminSftpTestFilesList {
  canDownload: boolean;
  testFiles: {
    lastModified: string;
    s3Key: string;
  }[];
}

export interface AdminSftpTestFileDownload {
  s3Key: string;
}

export interface PartnerSaveResponse {
  error: string;
  partner: PartnerSave;
}

export enum PartnerFunctionType {
  None = "none",
  Servicing = "servicing",
  Sales = "sales"
}

export interface TpaIdResponse {
  tpaIds: string[];
}

export enum BankCustodian {
  None = "none",
  Umb = "umb",
  Elements = "elements",
  Ucb = "ucb",
  Avidia = "avidia",
  Allegacy = "allegacy",
  HsaBank = "hsaBank",
  Omnibus = "omnibus"
}

export interface PartnerSave extends PartnerBilling {
  partnerId?: number;
  partnerName: string;
  partnerWebsite: string;
  partnerEin: string;
  partnerPhone: string;
  partnerAddress1: string;
  partnerAddress2: string;
  partnerCity: string;
  partnerState: string;
  partnerZip: string;
  partnerCustodian: BankCustodian;
  partnerCustodianLocked: boolean;
  ownerFirstName: string;
  ownerLastName: string;
  ownerPhone: string;
  ownerJobTitle: string;
  ownerEmail: string;
  partnerDisplayName: string;
  externalPartnerId: string;
  tpaId: string;
  functionTypes: PartnerFunctionType[];
  servicingPartners?: ServicingPartner[];
  automaticallyRemoveOrphans: boolean;
  investmentThreshold: PartnerInvestmentThreshold;
  individualMonthlyFeeAmount: number;
  individualFeeStartDate: string;
  individualEmployerId: number;
  feeWaiverType: FeeWaiverType;
  feeWaiverThreshold: number;
  investmentEnrollmentType: AllowedInvestmentAccountTypes;
  ssoDisabled: boolean;
  feeCollectionType: FeeCollectionType;
  ssoUserSyncStatus: boolean;
  storeConfiguration?: PartnerHsaStoreInfo;
}

export interface OpenSearchIndex {
  indexCount: number;
  isSearchEngineEnabled: boolean;
  isEmployerSearchEngineEnabled: boolean;
  isUserSearchEngineEnabled: boolean;
  isSearchEngineTypeAheadEnabled: boolean;
  domainName: string;
  s3Bucket: string;
  nameSynonymAnalyzer: string;
  packages: OpenSearchPackageDetail[];
  indexDetails: OpenSearchIndexDetail[];
}
export interface OpenSearchDomainDetail {
  customEndpoint: string;
  dedicatedMasterCount: number;
  dedicatedMasterType: string;
  domainName: string;
  engineVersion: string;
  endpoint: string;
  isAdvancedSecurityEnabled: boolean;
  isEncryptedAtRest: boolean;
  isEbsEnabled: boolean;
  isZoneAwarenessEnabled: boolean;
  isWarmEnabled: boolean;
  isHttpsEnforced: boolean;
  isDedicatedMasterEnabled: boolean;
  instanceCount: number;
  instanceType: string;
  volumeSize: number;
  volumeType: string;
  warmCount: number;
  warmType: string;
  zoneAvailabilityCount: number;
}
export interface OpenSearchPackageDetail {
  lastUpdated: string;
  domainName: string;
  packageId: string;
  packageName: string;
  status: string;
  packageType: string;
  packageVersion: string;
  referencePath: string;
  errorMessage: string;
}
export interface OpenSearchPackagesResponse {
  isPackageCreated: bool;
  parameterValue: string;
}
export interface OpenSearchIndexRequest {
  index: string;
  partnerId: number;
  employerId: number;
  dropAndRecreateIndex: boolean;
}

export interface OpenSearchPropertyDetail {
  name: string;
  fieldType: string;
}

export interface OpenSearchIndexDetail {
  name: string;
  canReIndex: boolean;
  lastIndexed: string;
  aliases: string[];
  properties: OpenSearchPropertyDetail[];
}

export interface OpenSearchIndexState {
  fetchState: FetchState;
  openSearchIndex: OpenSearchIndex;
  openSearchDomainDetails: OpenSearchDomainDetail;
  domainDetailsFetchState: FetchState;
  parameterValue: string;
  userSearchDocument: UserSearchDocument;
  employerSearchDocument: EmployerSearchDocument;
}

export interface StateMachineExecutionHistory {
    stateMachineArn: string;
    name: string;
    dateCreated: string;
    executionHistories: StateMachineExecutionHistoryItem[];
}

export interface StateMachineExecutionHistoryItem {
  executionArn: string;
  dateStarted: string;
  dateStopped: string;
  status: StateMachineStatus;
}
export interface EmailTemplateState {
  fetchState: FetchState;
  emailTemplates: EmailTemplate[];
  emailTemplateDetail: EmailTemplateDetail;
}
export interface EmailTemplate {
  name: string;
  displayName: string;
  created: string;
}

export interface EmailTemplateDetail {
  name: string;
  html: string;
  text: string;
  subject: string;
  htmlProperites: string[];
}

export enum StateMachineStatus {
    None = "none",
    Succeeded = "succeeded",
    Aborted = "aborted",
    Failed = "failed",
    TimedOut = "timedOut",
    Running = "running"
}

export interface StateMachineState {
  fetchState: FetchState;
  stateMachineExecutionHistories: StateMachineExecutionHistory[];
}

export interface UtilityState {
  fetchState: FetchState;
  testUsersFetchState: FetchState;
  testUsers: TestUser[];
  finicityLinkedAccountsFetchState: FetchState;
  finicityLinkedAccounts: FinicityLinkedAccount[];
  transactionCodeUploadId: string;
  transactionCodeProcessingState: FileProcessingState;
  transactionCodeExportFetchState: FetchState;
  cpfBlackoutDates: string[];
  cpfBlackoutDatesFetchState: FetchState;
}

export enum FileProcessingState {
  Uninitialized,
  Processing,
  Complete
}

export interface FinicityLinkedAccount {
 accountNumber: string;
 accountType: string;
 finicityInstitutionId?: number;
 isDeleted: boolean;
 customerId: string;
 finicityAccountId: string;
 institutionLoginId?: number;
 userActionRequired: boolean;
 dateCreated: string;
 finicityDateLastModified?: string;
 lastFinicityErrorCode?: number;
 isOAuth: boolean;
}

export interface TestUser {
  groupName: string;
  cognitoUsername: string;
  emailAddress: string;
}

export interface UserSearchState {
  fetchState: FetchState;
  pageNumber: number;
  totalRecords: number;
  elapsedMilliseconds: number;
  maxScore: number;
  users: UserSearchResultModel[];
  userPages: UserSearchResultModel[][];
}

export interface UmbAccountUser {
  emailAddress: string;
  cognitoUsername: string;
  firstName: string;
  lastName: string;
}

export interface UmbAccountUserModel {
  umbAccountUserId: number;
  cognitoUsername: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  employer: string;
}

export interface UmbAccountUserList {
  users: UmbAccountUserModel[];
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
}

export interface UmbAccountUserSearchState {
  fetchState: FetchState;
  pageNumber: number;
  totalRecords: number;
  users: UmbAccountUserModel[];
  userPages: UmbAccountUserModel[][];
}

export interface UmbReopenAccountRequest {
  cognitoUsername: string;
  tpaId: string;
}

export enum UmbLinkedAccountSearchCategory {
  byAccount,
  byEmail,
  byName
}

export interface AdminUmbLinkedAccountSearchRequest {
  accountNumber: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  searchCategory: UmbLinkedAccountSearchCategory;
}

export interface AdminUmbLinkedAccountSearchResponse {
  firstName: string;
  lastName: string;
  hsaAccountNumber: string;
  emailAddress: string;
  linkedAccounts: UmbLinkedAccount[];
}

export interface UmbDeleteLinkedAccountState {
  firstName: string;
  lastName: string;
  hsaAccountNumber: string;
  emailAddress: string;
  fetchState: FetchState;
  linkedAccounts: UmbLinkedAccount[];
}

export interface UmbLinkedAccount {
  linkedBankAccountId: number;
  bankAccountName: string;
  bankAccountNumber: string;
  bankRoutingNumber: string;
  dateCreated: string;
  dateVerified: string;
  dateLastModified: string;
  isDeleted: boolean;
  isDeletedAtBank: boolean;
  verificationStatus: LinkedBankAccountVerificationStatus;
  linkedBankAccountType: LinkedAccountType;
}

export interface AccountEnableDisableRequest {
  emailAddress: string;
}

export interface CognitoAccountEnableDisableResult {
  foundCognitoUsers: string[];
  disabledBendUsers: string[];
  enabledBendUsers: string[];
}

export interface UserDobNameModel {
  userId: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface UserSearchResultModel {
  cognitoUsername: string;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  isConfirmed: boolean;
  activeEmployer: string;
  sellingPartner: string;
}

export interface UserSearchResultList {
  users: UserSearchResultModel[];
  totalRecords: number;
  maxScore: number;
  elapsedMilliseconds: number;
  pageSize: number;
  pageNumber: number;
}

export interface UserListActionButtonOptions {
  show: boolean;
  text: string;
  disabled?: (user: any) => boolean;
  click?: (user: any) => void;
  to?: (id: any) => Location;
}

export interface FraudCheckState {
  fraudCheckUsers: FraudCheckUser[];
  pageNumbers: { index: number; hasMore: boolean; pageSize: number; }[];
  currentFraudCheckUser: FraudCheckUser;
  currentQueueId: number;
  searchTerm: string;
  fraudCheckLogs: FraudCheckActionLog[];
}

export interface FraudCheckPage {
  hasMore: boolean;
  items: FraudCheckUser[];
  isLoading: boolean;
}

export interface AddressOverride {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export interface FraudCheckQueueDataOverride {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  ssn?: string;
  hubspotTicket: string;
  physicalAddress?: AddressOverride;
  mailingAddress?: AddressOverride;
  physicalAndMailingAreSame: boolean;
}

export enum FraudCheckStatus {
  None = "none",
  FraudCheckRequired = "fraudCheckRequired",
  Pass = "pass",
  Fail = "fail",
  Error = "error",
  DocumentsRequested = "documentsRequested",
  FraudCheckNotRequired = "fraudCheckNotRequired",
}


// FraudDetectionService/FraudDetectionService.Contracts/V1/EnrollmentType.cs
export enum FraudCheckEnrollmentType {
  None = "none",
  Individual = "individual",
  Employee = "employee",
  AutoEnrollment = "autoEnrollment"
}

export interface FraudCheckUser {
  queueId: number;
  custodian: BankCustodian;
  cognitoUsername: string;
  dateCreated: string;
  dateOfBirth: string;
  emailAddress: string;
  phone: string;
  firstName: string;
  middleName: string;
  lastName: string;
  mailingAddress: Address;
  physicalAddress: Address;
  familyMembers: FamilyMember[];
  fraudCheckStatus: FraudCheckStatus;
  emailOfSsnConflict: string;
  employerName: string;
  enrollmentType: FraudCheckEnrollmentType;
  adjudicationStatus: FraudCheckAdjudicationStatus;
}

export interface FraudCheckActionLog {
  fraudQueueActionLogId: number;
  actionType: string;
  identityResultIdNumber: string;
  dateCreated: string;
  updatedBy: string;
  fraudCheckStatus: FraudCheckStatus;
  adjudicationStatus: FraudCheckAdjudicationStatus;
  custodian: BankCustodian;
  enrollmentType: FraudCheckEnrollmentType;
  employerName: string;
  phone: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  dateOfBirth: string;
  ssnOverride: string;
  fullPhysicalAddress: string;
  hubspotTicket: string;
}

export interface FamilyMember {
  firstName: string;
  lastName: string;
}

export interface FraudCheckQueueListResult {
  fraudCheckUsers: FraudCheckUser[];
  hasMore: boolean;
  pageSize: number;
  pageNumber: number;
  searchTerm: string;
}

export interface PendingPersonalContributionState {
  fetchState: FetchState;
  pageNumber: number;
  items: PendingPersonalContribution[];
  detailItem: PendingPersonalContribution;
}

export interface TransactionEditState {
  fetchState: FetchState;
  searchResult: TransactionEditSearchResult;
}

export interface SalesPerson {
  salesPersonId: number;
  firstName: string;
  lastName: string;
  dateCreated: string;
  dateLastModified: string;
  isActive: boolean;
}

export interface SalesPersonState {
  getRequestFetchState: FetchState;
  postRequestFetchState: FetchState;
  salesPersons: SalesPerson[];
}

export interface MfaAdminDisableRequest {
  emailAddress: string;
  hubspotTicketId: string;
}

export interface InvestmentAccountRepointState {
  getRequestFetchState: FetchState;
  postRequestFetchState: FetchState;
  hsaAccounts: HsaAccountDetails[];
  investmentRepointRequests: InvestmentRepointRequest[];
  investmentRepointRequestPages: InvestmentRepointRequest[][];
  pageNumber: number;
  pageCount: number;
}

export interface InvestmentRepointRequest {
  firstName: string;
  lastName: string;
  last4Ssn: string;
  fromHsaAccount: string;
  toHsaAccount: string;
  dateCreated: string;
  statusDescription: string;
  status: InvestmentRepointStatus;
}

export enum InvestmentRepointStatus {
  Pending = 0,
  Completed = 1,
  Failed = 2
}

export interface InvestmentRepointRequestResult {
  pageCount: number;
  pageNumber: number;
  investmentRepointRequests: InvestmentRepointRequest[];
}

export interface HsaAccountDetails {
  hsaAccountId: number;
  firstName: string;
  lastName: string;
  employerName: string;
  partnerName: string;
  hsaAccountNumber: string;
  hsaAccountStatus: UserHsaAccountStatus;
  hasInvestments: boolean;
  cognitoName: string;
  investmentFetchState: FetchState;
  investmentAccounts: InvestmentAccountDetails[];
}

export interface InvestmentAccountDetails {
  cognitoUsername: string;
  accountNumber: string;
  accountDescription: string;
  currentBalance: number;
  isActive: boolean;
}

export interface InvestmentAccountRepointRequest {
  fromAccount: number;
  toAccount: number;
}

export interface PartnerInvestmentThreshold {
  partnerId: number;
  investmentProvider: InvestmentCustodian;
  individual: InvestmentThreshold;
  employer: InvestmentThreshold;
}

export enum FeeWaiverType {
  None = "none",
  Balance = "balance",
  BalanceAndInvestment = "balanceAndInvestment"
}

export interface AdvisorAction {
  actionId: number;
  name: string;
  campaign: string;
  template: string;
  subjectTemplate: string;
  delayType: AdvisorDelayType;
  delayValue: number;
  delayPage: AdvisorDelayPage;
  priority: number;
  deliveryChannel: AdvisorDeliveryChannel;
  deliveryPage: AdvisorDeliveryPage;
  mediaFiles: any[];
  mediaFileNames: string[];
  link: string;
  linkText: string;
  isRouteLink: boolean;
  isLinkTargetNew: boolean;
  testMode: boolean;
}

export interface AdvisorState {
  actionFetchState: FetchState;
  action: AdvisorAction;
  actions: AdvisorAction[];
  actionFetchAllState: FetchState;
  forceListRefresh: boolean;
  ruleFetchAllState: FetchState;
  ruleFetchState: FetchState;
  rulePreviewFetchState: FetchState;
  rule: Advisor3Rule;
  rules: Advisor3Rule[];
  previewResult: AdvisorRulePreviewResult;
  previewTemplateFetchState: FetchState;
  previewTemplateResult: AdvisorPreviewTemplateResponse[];
}

export interface ActionMediaParams {
  fileName: string;
  actionId: number;
}

export enum FraudCheckAdjudicationStatus {
  None = "none",
  NeedsReview = "needsReview",
  InProcess = "inProcess",
  ReviewComplete = "reviewComplete",
  AutoReviewed = "autoReviewed",
  AwaitingDocuments = "awaitingDocuments",
  ChallengeQuestions = "challengeQuestions",
}

export enum FraudCheckQueueFilterColumn {
  Unknown = "unknown",
  Custodian = "custodian",
  EnrollmentType = "enrollmentType",
  FraudCheckStatus = "fraudCheckStatus",
  AdjudicationStatus = "adjudicationStatus"
}

export interface FraudCheckQueueFilterModel {
  field: FraudCheckQueueFilterColumn;
  values: string[];
}

export interface DebitCardCustomization {
  custodian: BankCustodian;
  customizationId?: number;
  cardStockOpaqueId: string;
  cardStockNickname: string;
}

export interface DebitCardCustomizationCreateRequest {
  custodian: BankCustodian;
  cardStockOpaqueId: string;
  CardStockNickname: string;
}

export interface DebitCardCustomizationUpdateRequest {
  custodian: BankCustodian;
  customizationId: number;
  CardStockNickname: string;
}

export interface PartnerCardCustomizationUpdateRequest {
  custodian: BankCustodian;
  partnerId: number;
  customizationId?: number;
}

export interface AdminPendingTxDeleteResponse {
  errorMessage: string;
}

export interface AdminPendingTxSearchRequest {
  accountNumber: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
}

export interface AdminPendingTxSearchResponse {
  accountName: string;
  errorMessage: string;
  pendingTxs: AdminPendingTx[];
}

export interface AdminPendingTx {
  bankPendingTransactionId: number;
  dateCreated: string;
  dateEffective: string;
  amount: number;
  description: string;
  transactionType: string;
  accountNumber: string;
  isDeleted: boolean;
}

export interface PartnerCardCusomizations {
  [partnerId: number]: {
    fetchState: FetchState,
    customization?: DebitCardCustomization
  };
}

export interface CardCustomizationState {
  cardCustomizationFetchState: FetchState;
  cardCustomizations: DebitCardCustomization[];
  partners: PartnerCardCusomizations;
}

export interface RiskQuizAgeRevision {
  minScore: number;
  maxScore: number;
  ageRevision: number;
}

export interface RiskQuizAgePortfolio {
  age: number;
  wlpId: string;
  clientId: string;
  portfolioId: string;
  name: string;
  description: string;
}

export interface RiskQuizScoreState {
  riskQuizAgeRevisionFetchState: FetchState;
  riskQuizAgePortfolioFetchState: FetchState;
  riskQuizAgeRevisions: RiskQuizAgeRevision[];
  riskQuizAgePortfolios: RiskQuizAgePortfolio[];
}

export enum ListType {
  None = 0,
  CuratedRtqList = 1
}

export interface InstrumentUploadResponse {
  deletedSymbols: number;
  addedSymbols: number;
  unknownSymbols: number;
  totalSymbols: number;
  s3DownloadUrl: string;
}

export interface InstrumentMetadataResponse {
  noChange: number;
  updated: number;
  deleted: number;
  added: number;
  notFound: number;
  total: number;
  s3DownloadUrl: string;
}

export interface InstrumentList {
  listName: string;
  listDescription: string;
  listInvestmentOption: DrivewealthAccountManagementType;
  listId: number;
  isDefault: boolean;
  count: number;
}

export interface WexProxyState {
  statusFileFetchState: FetchState;
}

export interface InstrumentListState {
  instrumentListFetchState: FetchState;
  curatedRtqInstrumentListFetchState: FetchState;
  instrumentDetailFetchState: FetchState;
  instrumentLists: InstrumentList[];
  curatedRtqInstrumentLists: InstrumentList[];
  instrumentUploadResponse: InstrumentUploadResponse;
  instrumentDetails: InstrumentDetail[];
  employerInstrumentList: PartnerEmployerInstrumentList;
  employerInstrumentListFetchState: FetchState;
  partnerInstrumentList: PartnerEmployerInstrumentList;
  partnerInstrumentListFetchState: FetchState;
  wlpInstrumentListsFetchState: FetchState;
  custodianWlpInstrumentListsFetchState: FetchState;
  employerDefaultInstrumentLists: InstrumentList[];
  custodianDefaultInstrumentLists: InstrumentList[];
}

export interface HsaAccountStatusUpdateResponse {
  noChange: number;
  updated: number;
  notFound: number;
  total: number;
  s3DownloadUrl: string;
}

export interface InvestmentAccountCloseState {
  investmentAccountCloseFileUploadState: FetchState;
  investmentAccountCloseFetchState: FetchState;
}

export interface EmployerDrivewealthListRequest {
  employerId: number;
  investmentOption: DrivewealthAccountManagementType;
  instrumentListId: number;
}

export interface CustodianDrivewealthListRequest {
  custodian: number;
  investmentOption: DrivewealthAccountManagementType;
  instrumentListId: number;
}

export interface PartnerEmployerInstrumentList {
  partnerAdvisoryListId: number;
  partnerOpenBrokerageListId: number;
  partnerAdvisoryListName: string;
  partnerOpenBrokerageListName: string;
  availableLists: InstrumentList[];
}

export enum PartnerContentType {
  DrivewealthInvestmentPromo = "drivewealthInvestmentPromo",
  Footer = "footer",
  PostLoginFooter = "postLoginFooter"
}
export interface BankAccountBlacklistState {
  fetchState: FetchState;
  bankAccountBlacklistResponse: BankAccountBlacklistResponse;
}
export interface BankAccountBlacklistResponse {
  blacklists: AccountListItem[];
}
export interface AccountListItem {
  bankAccountBlacklistId: number;
  accountNumber: string;
  routingNumber: string;
  bankName: string;
}
export interface AvidiaPartner {
  partnerId: number;
  costCenterId: number;
  branchId: number;
}

export interface AvidiaPartners {
  [partnerId: number]: {
    fetchState: FetchState,
    avidiaPartner?: AvidiaPartner
  };
}

export interface AvidiaPartnersState {
  partners: AvidiaPartners;
}

export enum UserHsaAccountSubStatus {
  None = "none",
  Active = "active",
  BadAddress = "badAddress",
  Dormant = "dormant",
  Deceased = "deceased",
  Closed = "closed",
  Escheatable = "escheatable",
  Inactive = "inactive",
  Pending = "pending",
}

export const accountSubStatusMap: Record<UserHsaAccountSubStatus, string> = {
  none: "None",
  active: "Active",
  badAddress: "Bad Address",
  dormant: "Dormant",
  deceased: "Deceased",
  closed: "Closed",
  escheatable: "Escheatable",
  inactive: "Inactive",
  pending: "Pending"
};

export enum TransactionStatusType {
  None = "none",
  PayrollContribution = "payrollContribution",
  BatchPayrollContribution = "batchPayrollContribution",
  Reimbursement = "reimbursement",
  BatchReimbursement = "batchReimbursement",
  DepositInvestment = "depositInvestment",
  BatchDepositInvestment = "batchDepositInvestment",
  WithdrawInvestment = "withdrawInvestment",
  BatchWithdrawInvestment = "batchWithdrawInvestment",
  ReverseDepositInvestment = "reverseDepositInvestment",
  BatchReverseDepositInvestment = "batchReverseDepositInvestment",
  PersonalContribution = "personalContribution",
  BatchPersonalContribution = "batchPersonalContribution",
  FeeCollection = "feeCollection",
  BatchFeeCollection = "batchFeeCollection",
  FeeReversal = "feeReversal",
  BatchFeeReversal = "batchFeeReversal",
  PennyDeposit = "pennyDeposit"
}
export const transactionTypeMap: Record<TransactionStatusType, string> = {
  none: "None",
  payrollContribution: "Payroll Contribution",
  batchPayrollContribution: "Batch Payroll Contribution",
  reimbursement: "Reimbursement",
  batchReimbursement: "Batch Reimbursement",
  depositInvestment: "Deposit Investment",
  batchDepositInvestment: "Batch Deposit Investment",
  withdrawInvestment: "Withdraw Investment",
  batchWithdrawInvestment: "Batch Withdraw Investment",
  reverseDepositInvestment: "Reverse Deposit Investment",
  batchReverseDepositInvestment: "Batch Reverse Deposit Investment",
  personalContribution: "Personal Contribution",
  batchPersonalContribution: "Batch Personal Contribution",
  feeCollection: "Fee Collection",
  batchFeeCollection: "Batch Fee Collection",
  feeReversal: "Fee Reversal",
  batchFeeReversal: "Batch Fee Reversal",
  pennyDeposit: "Penny Funding"
};

export enum IntraBankTransactionMessageTypes {
  Unknown = "unknown",
  Scheduled = "scheduled",
  InProcess = "inProcess",
  Processed = "processed",
  NonSufficientFunds = "nonSufficientFunds",
  Canceled = "canceled",
  Error = "error",
  AccountLocked = "accountLocked",
  Timeout = "timeout",
  HoldingAccountOverdrawn = "holdingAccountOverdrawn"
}

export const failureReasonMap: Record<IntraBankTransactionMessageTypes, string> = {
  unknown: "Unknown",
  scheduled: "Scheduled",
  inProcess: "In Process",
  processed: "Processed",
  nonSufficientFunds: "Overdrawn",
  canceled: "Canceled",
  error: "Error",
  accountLocked: "Account Locked",
  timeout: "Timeout",
  holdingAccountOverdrawn: "Holding Account Overdrawn",
};

export interface InstantTransferFailureResponseModel {
  amount: number;
  transactionId: string;
  firstName: string;
  lastName: string;
  custodian: BankCustodian;
  dateCompleted: string;
  dateLastModified: string;
  employerName: string;
  partnerName: string;
  messageType: TransactionStatusType;
  transactionStatus: IntraBankTransactionMessageTypes;
}

export interface InstantTransferFailureDetailRetryModel {
    transactionStatus: IntraBankTransactionMessageTypes;
    dateCompleted: string;
    custodianTransactionId: string;
}

export interface InstantTransferFailureDetailModel extends InstantTransferFailureResponseModel {
  accountNumber: string;
  routingNumber: string;
  cognitoUsername: string;
  employmentStatus: EmploymentStatus;
  accountStatus: UserHsaAccountStatus;
  accountSubStatus: UserHsaAccountSubStatus;
  dateInitialFailure: string;
  dateLastFailure: string;
  isArchived: boolean;
  balanceSnapshot: number;
  availableBalance: number;
  retries: InstantTransferFailureDetailRetryModel[];
  isRetriable: boolean;
  custodianErrorResponse: string;
}
export interface InstantTransferPageResponseModel {
  items: InstantTransferFailureResponseModel[];
  pageNumber: number;
  numberOfPages: number;
  totalCount: number;
}
export interface InstantTransferFailureRequest {
  pageNumber: number;
  pageSize: number;
  isArchived: boolean;
  fromDate: string;
  toDate: string;
  amount: number;
  statuses: IntraBankTransactionMessageTypes[];
  types: TransactionStatusType[];
}
export interface InstantTransferFailureState {
  items: InstantTransferFailureResponseModel[];
  listFetchState: FetchState;
  itemFetchState: FetchState;
  pageNumber: number;
  numberOfPages: number;
  totalCount: number;
  transactionId: string;
  detailItem: InstantTransferFailureDetailModel;
  filters: InstantTransferFailureRequest;
}
export interface EmploymentStatusResponse {
  employerId: number;
  employmentStatus: EmploymentStatus;
}
export interface ResetUserCustodianRequest {
  emailAddress: string;
  oldCustodian: BankCustodian;
  newCustodian: BankCustodian;
}

export interface EmployerThemeRequest {
  employerId: number;
  themeId: number;
  delete: boolean;
}

export interface EmployerThemeResponse {
  employerThemeId: number;
  employerId: number;
  themeId: number;
}

export interface DemoRosterSaveRequest {
  emails: string[];
}

export interface DemoRosterRecord {
  firstName: string;
  lastName: string;
  email: string;
  partnerName: string;
  custodianName: string;
}

export interface DemoRosterSaveResponse {
  emailsNotFound: string[];
  roster: DemoRosterRecord[];
}

export interface DemoRosterResponse {
  openBrokerage: DemoRosterRecord[];
  riaManaged: DemoRosterRecord[];
}

export interface DemoRosterState {
  roster: DemoRosterRecord[];
  fetchState: FetchState;
}

export interface DemoUsersState {
  rostersFetchState: FetchState;
  outOfDateFetchState: FetchState;
  areRostersOutOfDate: boolean | null;
  openBrokerage: DemoRosterState;
  riaManaged: DemoRosterState;
  emailsNotFound: string[];
}

export type DemoUserRosterName = "openBrokerage" | "riaManaged";
export const reimbursementTypeMap: Record<ReimbursementType, string> = {
  none: "Unknown",
  ach: "ACH",
  debitCard: "Debit Card",
  disbursement: "Disbursement",
};
export interface ReimbursementFailureResponseModel {
  firstName: string;
  lastName: string;
  employerName: string;
  partnerName: string;
  amount: number;
  reimbursementId: number;
  custodian: BankCustodian;
  dateFailed: string;
  dateInitiated: string;
  reimbursementType: ReimbursementType;
}
export interface ReimbursementFailureDetailModel extends ReimbursementFailureResponseModel {
  accountNumber: string;
  routingNumber: string;
  cognitoUsername: string;
  accountStatus: UserHsaAccountStatus;
  accountSubStatus: UserHsaAccountSubStatus;
  isArchived: boolean;
  isInProcess: boolean;
  email: string;
  ssnLastFour: string;
  bankTransactionId: string;
}
export interface ReimbursementFailurePageResponseModel {
  items: ReimbursementFailureResponseModel[];
  pageNumber: number;
  numberOfPages: number;
  totalCount: number;
}
export interface ReimbursementFailurePageRequestModel {
  pageNumber: number;
  pageSize: number;
  isArchived: boolean;
}
export interface ReimbursementFailureState {
  items: ReimbursementFailureResponseModel[];
  listFetchState: FetchState;
  itemFetchState: FetchState;
  pageNumber: number;
  numberOfPages: number;
  totalCount: number;
  reimbursementId: number;
  detailItem: ReimbursementFailureDetailModel;
}

export interface CampaignDataModel {
  key: string;
  Name: string;
  EmployerId: number;
  EmployerName: string;
  DateStart: Date;
  DateEnd: Date;
  DecisionCount: number;
  EmployeeCount: number;
  Link: string;
}

export interface CampaignState {
  FetchState: FetchState;
  CampaignData: CampaignDataModel[] | any;
  CurrentCampaign: CreateTransferOutCampaignRequest;
  campaignRetentionData: CampaignRetentionDataModel;
}

export interface EbtoEmployerSearchResult {
  employerSearchResults: EbtoEmployerResult[];
}

export interface EbtoEmployerResult {
  id: number;
  name: string;
  externalEmployerId: string;
}

export interface CreateTransferOutCampaignRequest {
  employerId: number;
  employerName: string;
  employerExtId: string;
  name: string;
  dateStart: Date;
  dateEnd: Date;
  dateDistributionsEnd: Date;
  dateCutOff: Date;
  dateTransfer: Date;
  newCustodian: string;
  newCustodianEffectiveDate: Date;
  bonus: number;
  isAdminFeeWaived: boolean;
}

export interface CampaignRetentionDataModel {
  totalAccountsRetained: number;
  totalDepositsRetained: number;
}

export interface PartnerAchBrandingRequest {
  companyName: string;
}

export interface PartnerAchBrandingResponse {
  companyName: string;
}

export interface EmployerMigrationPeriod {
  dateStart: Date;
  dateEnd: Date;
}

export interface PartnerAccountsMigrationModel {
  id: number;
  partnerId: number;
  dateStart: Date;
  dateEnd: Date;
  dateCreated: Date;
  dateLastModified: Date;
}

export enum CardPlasticType {
  MagneticStrip = "magneticStrip",
  Chip = "chip"
}

export const cardPlasticTypeValueMap: Record<CardPlasticType, string> = {
  magneticStrip: "001",
  chip: "002"
};

export const cardPlasticTypeMap: Record<CardPlasticType, string> = {
  magneticStrip: "Magnetic Strip",
  chip: "Chip"
};

export interface CardIssueConfig {
  issueCardCriteriaId: number;
  partnerId: number;
  dateEnabled: Date|null;
  cardPlasticType: CardPlasticType|null; // null until set by Admin user
  employerId: number|null; // only null for Partner-wide configs
  ssnLast4: string|null; // only null for Partner-wide and Employer-wide confgis
  firstName: string|null; // null except for User-specific configs when User is in CardService DB
  lastName: string|null; // null except for User-specific configs when User is in CardService DB
  email: string|null; // null except for User-specific configs when User is in CardService DB
}

export interface CardEmployerListItem {
  employerId: number;
  name: string;
}

export interface CardEmployerList {
  employers: CardEmployerListItem[];
}

export interface PartnerIndependentCardManagement {
  hasIndependentCardManagement: boolean;
}

export interface PartnerCardProviderSettingsState {
  partnerId: null|number;
  fetchStateHasIndependentCardManagement: FetchState;
  hasIndependentCardManagement: boolean;
}

export interface PartnerCardProviderSettingsRequest {
  partnerId: number;
  hasIndependentCardManagement: boolean;
}

export interface CardIssueConfigResponse {
  partnerId: number;
  partner: CardIssueConfig|null;
  employers: CardIssueConfig[];
  unsyncedUsers: CardIssueConfig[];
  syncedUsers: CardIssueConfig[];
}

export interface EmployerCardIssueCriteriaResponse {
  issueCardCriteriaId: number;
  dateEnabled: Date|null;
}

export interface AccountHolderIssueCardCriteriaResponse {
  unsyncedUsers: AccountHolderIssueCardCriteria [];
  syncedUsers: AccountHolderIssueCardCriteria [];
}

export interface AccountHolderIssueCardCriteria {
  issueCardCriteriaId: number|null;
  issueCardCustomizationId: number|null;
  dateEnabled: Date|null;
  firstName: string|null;
  lastName: string|null;
  email: string|null;
  ssnLast4: string|null;
}

export interface CardEmployerMap {
  [id: number]: CardEmployerListItem;
}

export interface PartnerCardIssueCriteriaState {
  partnerId: null|number;
  fetchState: FetchState;
  cardIssueConfigs: null|CardIssueConfigResponse;
  employersFetchState: FetchState;
  employers: CardEmployerMap;
  unconfiguredEmployers: CardEmployerListItem[];
  cardIssueCustomizations: {};
  employerCardIssueCriterias: {};
  accountHoldersIssueCardCriterias: {};
  cardIssueCustomizationOptionsFetchState: FetchState;
  cardIssueCustomizationOptions: CardIssueCustomizationOptionModel[];
}

export interface EmployerInfo {
  employerId: number;
  employerName: string;
}

export interface SsoUserSyncStatusModel {
  ssoUserSyncStatus: boolean;
}

export enum InvestmentFeeScheduleSettingsFormVariant {
  partner = "partner",
  employer = "employer"
}

export interface CustodianDefaultAgreementModel {
  drivewealthCustodianDefaultAgreementId: number;
  drivewealthCustodianAccountId: number;
  custodian: BankCustodian;
  accountType: DrivewealthAccountManagementType;
  documentKey: string;
  dateCreated: string;
  dateLastModified: string;
}

export interface EmployerAgreementModel {
  employerAgreementId: number;
  employerId: number;
  accountType: DrivewealthAccountManagementType;
  documentKey: string;
  dateCreated: string;
  dateLastModified: string;
}

export interface UpdateCustodianDefaultAgreementRequest {
  custodian: number;
  accountType: DrivewealthAccountManagementType;
  documentKey: string;
}

export interface UpdateEmployerAgreementRequest {
  employerId: number;
  accountType: DrivewealthAccountManagementType;
  documentKey: string;
}

export interface EmployerInvestmentEnrollmentTypeState {
  allowedEmployerInvestmentAccountTypes: AllowedEmployerInvestmentAccountTypes;
  fetchState: FetchState;
}

export enum CardIssueCustomizationOptionType {
  Unknown = "unknown",
  PlasticType = "plasticType",
  Logo = "logo",
  CardCarrierFrontTemplate = "cardCarrierFrontTemplate",
  CardActivationLabel = "cardActivationLabel",
  CardCriteriaPlan = "cardCriteriaPlan",
  TermedEmployeeCardStatus = "termedEmployeeCardStatus",
  FundPurseId = "fundPurseId",
  PlasticRushCode = "plasticRushCode",
}

export enum EmployeeDisposition {
  Default = 0,
  Terminated = 1
}

export const manageableCardCustomizationOptionTypes = [
  CardIssueCustomizationOptionType.Logo
];

export interface CardIssueCustomizationOptionModel {
  issueCardCustomizationOptionId: number;
  cardProvider: CardProvider;
  optionType: CardIssueCustomizationOptionType;
  name: string;
  value: string;
  isDefaultOption: boolean;
  isDeleted: boolean;
}

export interface CardIssueCustomizationOptionUpdateModel {
  issueCardCustomizationOptionId: number;
  optionType: CardIssueCustomizationOptionType;
  name: string;
  value: string;
  isDefaultOption: boolean;
}

export interface CardIssueCustomizationOptionUpdateRequest {
  options: CardIssueCustomizationOptionUpdateModel[];
  deletedOptions: number[];
}

export interface CardIssueCustomizationOptionsResponse {
  customizationOptions: CardIssueCustomizationOptionModel[];
}

export interface FeePackageState {
  feePackages: FeePackageModel[];
  fetchState: FetchState;
}

export interface PurseStackingOrderListState {
  purseStackingOrders: PurseStackingOrderModel[];
  fetchState: FetchState;
}

export enum PlasticRushCodeValue {
  NoSpecialHandling = "CardRushCodeNoSpecialHandling",
  DailyMass = "CardRushCodeDailyMass"
}

export interface PlasticRushCodeModel {
  plasticRushCodeDisplay: string;
  plasticRushCodeValue: string|null;
}

export interface CreateCardIssueCriteriaRequest {
    partnerId: number;
    employerId?: number;
    ssn?: string;
  }

export interface CreateCardIssueCriteriaResponse {
  issueCardCriteriaId: number|null;
}

export interface CardCustomizationModel {
  cardPlasticType: string|null;
  primaryPersonalizedEmbossingText: string|null;
  phoneNumber: string|null;
  url: string|null;
  issuerDefinedCardLogoCode: string|null;
  cardCarrierFrontTemplate: string|null;
  cardActivationLabel: string|null;
  feeScheduleId: string|null;
  cardProvider: CardProvider;
  fundPurseId: string|null;
  plansThatReceiveCards: any[];
  usersMovedToThisEmployerWillBeIssuedNewCards: boolean;
  employeeDisposition: EmployeeDisposition;
  purseStackingOrderValueId: number|null;
  plasticRushCode: string|null;
}

export interface SetCardCriteriaCustomizationRequest {
  default: CardCustomizationModel;
}

export interface IssueCardCustomizationResponse {
  default: CardCustomizationModel;
}

export interface FileUploadRequest {
  file: File;
}

export enum BulkFileUploadType {
  MassAdd = 1,
  MassDelete = 2
}

export enum BulkFileUploadStatus {
  None = "none",
  MessagePublished = "messagePublished",
  Error = "error",
  Processing = "processing",
  Completed = "completed",
  ErrorUploadingFile = "errorUploadingFile",
  Validating = "validating",
  Uploading = "uploading",
  Uploaded = "uploaded",
  Failed = "failed",
  ErrorPublishingMessage = "errorPublishingMessage",
  Pending = "pending"
}

export const bulkFileUploadStatusMap: Record<BulkFileUploadStatus, string> = {
  none: "Any",
  messagePublished: "Message Published",
  error: "Error",
  processing: "Processing",
  completed: "Completed",
  errorUploadingFile: "Error Uploading File",
  validating: "Validating",
  uploading: "Uploading",
  uploaded: "Uploaded",
  failed: "Failed",
  errorPublishingMessage: "Error Publishing Message",
  pending: "Pending"
};

export interface PartnerUserBulkResponse {
  bulkFiles: BulkUploadFile[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
}

export interface BulkUploadFile {
  partnerUserFileId: number;
  fileName: string;
  dateUploaded: string;
  dateModified: string;
  uploadedBy: string;
  status: BulkFileUploadStatus;
  fileType: string;
  fileNameUnique: string;
  numberOfRowsProcessed: number;
  numberOfRowsSkipped: number;
  numberOfRowsErrored: number;
  outputFileName: string;
  message: string;
}

export interface CardCustomizationBulkImportFileResponse {
  files: CardCustomizationBulkImportFile[];
}

export interface CardCustomizationBulkImportFile {
  fileId: number;
  fileName: string;
  dateUploaded: string;
  uploadedBy: string;
  status: CardCustomizationBulkImportFileStatus;
  newRecordCount: number;
  updatedRecordCount: number;
  errorRecordCount: number;
  isResultsFileAvailable: boolean;
}

export enum CardCustomizationBulkImportFileStatus {
  Unknown = "Unknown",
  Pending = "Pending",
  Success = "Success",
  Failed = "Failed"
}

export interface CardCustomizationBulkImportFileState {
  files: CardCustomizationBulkImportFile[];
  fetchState: FetchState;
}

export interface CardCustomizationBulkImportFileUploadRequest {
  file: File;
}

export interface CardCustomizationBulkImportResultsResponse {
  url: string;
}

export interface EmployerInterestRateState {
  fetchState: FetchState;
  employerId: number;
  interestRates: UserInterestRate[];
}

export interface EmployerFeeScheduleState {
  fetchState: FetchState;
  employerId: number;
  feeSchedules: EmployerFeeSchedule[];
}

export interface EmployerPrinSettingsResponse {
  externalEmployerId: string;
  prin: string;
}

export interface CardEmployerPrinState {
  fetchState: FetchState;
  externalEmployerId: string;
  prin: string;
}

export interface CardEmployerPrinSettingsRequest {
  employerId: number;
  prin: string;
}

export type BannerType = "default" | "custom";

export type BannerSettingsViewMode = "existing" | "create";

export interface NewPartnerBanner {
  titleA: string;
  titleB: string;
  description: string;
  linkUrl: string;
  linkText: string;
}

export interface PartnerBannerDraft {
  bannerId?: string;
  partnerId: string;
  titleA: string;
  titleB: string;
  description: string;
  linkUrl: string;
  linkText: string;
  carouselOrder?: number;
}

export interface CustomBannersRef {
  updateBanner?: (payload: PartnerBannerDraft) => Promise<void> | void;
  createBanner?: (payload: PartnerBannerDraft) => Promise<void> | void;
  copyBanner?: (banner: any) => void | Promise<void>;
}

export enum FiservCodeType {
  TierRate = "tierRate",
  TierAPY = "tierAPY",
  TierRange = "tierRange",
  HsaServiceFee = "hsaServiceFee",
  HsaServiceFeeWaiver = "hsaServiceFeeWaiver",
  PrintedSummaryFee = "printedSummaryFee",
  TermFee = "termFee",
  TermFeeWaiver = "termFeeWaiver"
}

export interface FiservCodeMappingRequest {
  type: FiservCodeType;
  tier: number;
  startValue: number;
  endValude?: number;
  code: string;
}

export interface FiservCodeMappingResponse {
  fiservCodeMappingId: number;
  type: FiservCodeType;
  tier: number;
  startValue: number;
  endValude?: number;
  code: string;
}

export interface FiservCodeMappingState {
  mappingsFetchState: FetchState;
  mappings: FiservCodeMappingResponse[];
}

export interface AdminHsaStoreInfoStore {
  hsaStoreInfoFetchState: FetchState;
  partnerHsaStoreInfoFetchState: FetchState;
  employerHsaStoreInfoFetchState: FetchState;
  defaultHsaStoreInfoFetchState: FetchState;
  defaultHsaStoreInfos: HsaStoreVisibility[];
  hsaStoreInfos: HsaStoreVisibility[];
  partnerHsaStoreInfos: HsaStoreVisibility[];
  employerHsaStoreInfos: HsaStoreVisibility[];
}

export interface BrowserRule {
  browser: BrowserIdentifier;
  minVersion: number;
  enabled: boolean;
  dateCreated: string;
  createdBy: string;
  dateLastModified: string;
  lastModifiedBy: string;
}

export interface BrowserRules {
  browserRules: BrowserRule[];
}

export interface BrowserRuleAddRequest {
  browser: BrowserIdentifier;
  minVersion: number;
  enabled: boolean;
}

export interface BrowserRuleUpdateRequest {
  minVersion: number;
  enabled: boolean;
}

export interface BrowserRuleState {
  fetchState: FetchState;
  browserRules: BrowserRule[];
}
